import { cva, type VariantProps } from 'class-variance-authority'
import { Input as AriaInput, type InputProps as AriaInputProps } from 'react-aria-components'
import { cn } from '../lib/cn'

/**
 * Variant styles for {@link Input}.
 *
 * All sizing, spacing, and color classes resolve to design tokens from @today/tokens
 * (resolved per bound brand). No hardcoded values.
 *
 * @remarks
 * - Supports three sizes: sm (h-8), md (h-10, default), lg (h-12)
 * - Fluid width by default (`w-full`)
 * - Focus ring uses the bound brand's primary colour
 *
 * @example
 * ```tsx
 * const classes = inputVariants({ size: 'md' })
 * // Returns: "w-full rounded-ds-md border ... h-10 ..."
 * ```
 */
export const inputVariants = cva(
	// The invalid state is driven by `aria-invalid`, which FieldShell injects onto the
	// control when an error is present — so the ds-danger border lights up with no extra
	// prop to wire, and a manual `aria-invalid` on a bare <Input> styles identically.
	'w-full rounded-ds-md border border-ds-border bg-ds-surface px-3 text-ds-base text-ds-text transition-colors placeholder:text-ds-text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring disabled:cursor-not-allowed disabled:opacity-50 aria-[invalid=true]:border-ds-danger aria-[invalid=true]:focus-visible:ring-ds-danger',
	{
		variants: {
			size: {
				sm: 'h-8 text-ds-sm px-2',
				md: 'h-10 text-ds-base px-3',
				lg: 'h-12 text-ds-lg px-4',
			},
		},
		defaultVariants: { size: 'md' },
	},
)

/**
 * Props for the {@link Input} component.
 *
 * Extends React Aria's InputProps with CVA size variants.
 *
 * @property size - Input size: 'sm' (h-8) | 'md' (h-10, default) | 'lg' (h-12)
 * @property className - Optional additional Tailwind classes
 * @property placeholder - Placeholder text
 * @property disabled - Disabled state
 * @property readOnly - Read-only state
 * @property required - Required field indicator
 *
 * @example
 * ```tsx
 * <Input
 *   size="md"
 *   placeholder="Enter your email"
 *   type="email"
 *   required
 * />
 * ```
 */
export interface InputProps extends Omit<AriaInputProps, 'className' | 'size'>, VariantProps<typeof inputVariants> {
	className?: string
}

/**
 * Accessible text input primitive using React Aria Components.
 *
 * @remarks
 * - **Accessibility:** Keyboard navigation, focus management, and ARIA attributes
 *   provided by React Aria's `<Input>` primitive.
 * - **Styling:** All colors, spacing, and sizing use design tokens from `@today/tokens`
 *   (resolved per bound brand). No hardcoded values.
 * - **Sizes:** Small (h-8), medium (h-10, default), large (h-12).
 * - **Fluid width:** Uses `w-full` by default to fill container.
 * - **Focus indicator:** 2px ring in the bound brand's primary colour.
 * - **Invalid state:** when `aria-invalid="true"` is present — set automatically by
 *   `FieldShell` whenever it has an error — the border and focus ring turn `ds-danger`.
 *
 * @example Basic usage
 * ```tsx
 * import { Input } from '@today/ui-primitives'
 *
 * function MyForm() {
 *   return (
 *     <form>
 *       <Input
 *         type="email"
 *         placeholder="Email address"
 *         required
 *       />
 *       <Input
 *         type="password"
 *         placeholder="Password"
 *         size="lg"
 *       />
 *     </form>
 *   )
 * }
 * ```
 *
 * @example With disabled state
 * ```tsx
 * <Input
 *   value="Read-only value"
 *   disabled
 * />
 * ```
 *
 * @see {@link inputVariants} for CVA variant configuration
 * @see packages/ui-primitives/src/input/input.stories.tsx for visual examples
 */
export function Input({ size, className, ...props }: InputProps) {
	return <AriaInput {...props} className={cn(inputVariants({ size }), className)} />
}
